<script lang="ts">
	import { Section, SectionHeader } from "#lib/web-ui/primitives/index.js";
	import type { PortfolioAssetCardsProps } from "./portfolio-map.types";
	import { assetTypeColors, capitalStructureColors } from "./portfolio-map.types";
	import { cloudinaryFetch } from "#lib/shared/cloudinary.js";
	import BuildingIcon from "@lucide/svelte/icons/building-2";

	let {
		assets,
		label,
		title,
		subtitle,
		theme = "light",
		class: className
	}: PortfolioAssetCardsProps = $props();

	let failedImages = $state(new Set<string>());
</script>

<Section {theme} class={className}>
	<SectionHeader {label} {title} {subtitle} />

	<div class="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
		{#each assets as asset (asset.id)}
			{@const color = assetTypeColors[asset.assetType]?.hex ?? "#6b7280"}
			{@const structureColor =
				capitalStructureColors[asset.capitalStructure]?.hex ?? "#6b7280"}
			<div
				class="bg-card text-card-foreground group border-border flex flex-col overflow-hidden rounded border shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md"
			>
				<div class="bg-muted relative h-44 shrink-0 overflow-hidden">
					{#if asset.imageUrl && !failedImages.has(asset.id)}
						<img
							src={cloudinaryFetch(asset.imageUrl, { width: 400 })}
							alt={asset.displayName}
							class="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
							width="400"
							height="176"
							loading="lazy"
							onerror={() => {
								failedImages = new Set([...failedImages, asset.id]);
							}}
						/>
					{:else}
						<div class="flex h-full w-full items-center justify-center">
							<BuildingIcon class="text-muted-foreground/40 size-12" />
						</div>
					{/if}
					{#if asset.imageUrl && !failedImages.has(asset.id)}
						<div
							class="absolute inset-0 bg-linear-to-t from-black/40 to-transparent"
						></div>
					{/if}
					<div class="absolute bottom-0 left-0 z-10 flex items-center gap-2 p-3">
						<span
							class="inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold text-white"
							style="background-color: {color};"
						>
							{asset.assetType}
						</span>
						<span
							class="inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold text-white"
							style="background-color: {structureColor};"
						>
							{asset.capitalStructure}
						</span>
					</div>
				</div>

				<div class="flex grow flex-col p-5">
					<p class="text-card-foreground text-lg font-semibold">{asset.displayName}</p>
					<p class="text-muted-foreground mt-1 text-sm">
						{asset.city}, {asset.state}
					</p>

					<div
						class="text-muted-foreground border-border mt-4 space-y-1.5 border-t pt-4 text-sm"
					>
						{#if asset.purchaseDate}
							<div class="flex justify-between">
								<span>Acquired</span>
								<span class="text-card-foreground font-medium"
									>{new Date(asset.purchaseDate).getFullYear()}</span
								>
							</div>
						{/if}
						{#if asset.assetStatus}
							<div class="flex justify-between">
								<span>Status</span>
								<span class="text-card-foreground font-medium"
									>{asset.assetStatus}</span
								>
							</div>
						{/if}
						{#if asset.unitCount}
							<div class="flex justify-between">
								<span>Units</span>
								<span class="text-card-foreground font-medium"
									>{asset.unitCount}</span
								>
							</div>
						{/if}
						{#if asset.yearBuilt}
							<div class="flex justify-between">
								<span>Year Built</span>
								<span class="text-card-foreground font-medium"
									>{asset.yearBuilt}</span
								>
							</div>
						{/if}
						{#if asset.grossLeasableArea}
							<div class="flex justify-between">
								<span>GLA</span>
								<span class="text-card-foreground font-medium">
									{asset.grossLeasableArea.toLocaleString()} SF
								</span>
							</div>
						{/if}
					</div>
				</div>
			</div>
		{/each}
	</div>
</Section>
